import { Link } from 'react-router-dom';
import type { ProjectView } from '@/types/current-project';
import type { ProjectPayment } from '@/lib/current-payments';
import { projectPayments, paymentTotal } from '@/lib/current-payments';
import { useAuthStore } from '@/stores/authStore';
import { useProjectParticipants } from '@/hooks/useProjectViews';
import { profileImageUrl } from '@/hooks/useProfile';
import { Avatar, Badge, Card, CardContent } from '@/components/ui';
import {
  executionMilestones,
  weekWindowLabel,
} from '@/lib/project-presentation';
import { useDaoConfig } from '@/hooks/useDaoConfig';

export function MilestonePaymentItem({
  project,
  payment,
}: {
  project: ProjectView;
  payment: ProjectPayment;
}) {
  const { currency } = useDaoConfig();
  const participants = useProjectParticipants(project.projectId);
  const client = participants.data?.client;
  const clientName =
    client?.profiles?.client?.name ?? client?.displayName ?? 'Client';
  const milestone = executionMilestones(project).find(
    (m) => m.milestoneId === payment.key
  );
  return (
    <Link
      to={'/payments/' + project.projectId}
      className="flex items-start justify-between gap-[--spacing-8] rounded-[--radi-6] border border-[--base-border] bg-transparent p-[--spacing-8] text-left hover:border-[--state-brand-active] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[--state-brand-active]"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-[--spacing-8]">
        <div className="flex flex-wrap items-center gap-[--spacing-5] text-lg font-medium">
          <div className="flex min-w-0 items-center gap-[--spacing-5]">
            <Avatar
              src={
                client?.clientImage && client.profiles
                  ? profileImageUrl(client.profiles.principalId, 'client')
                  : undefined
              }
              alt={clientName}
              size="sm"
              className="shrink-0"
            />
            <span className="break-words text-[--text-dark-primary]">
              {clientName}
            </span>
          </div>
          <span className="text-[--text-dark-secondary]">for</span>
          <span className="break-words text-[--state-brand-active]">
            {project.title}
          </span>
        </div>
        <div className="flex min-w-0 flex-col gap-[--spacing-5]">
          <h4 className="text-start text-lg font-semibold text-[--text-dark-primary]">
            {payment.title}
          </h4>
          {milestone && (
            <p className="text-lg font-medium text-[--text-dark-secondary]">
              {weekWindowLabel(milestone.definition.window)}
            </p>
          )}
          {payment.frozen && !payment.paid && (
            <p className="text-xs text-amber-400">Payment held</p>
          )}
        </div>
      </div>
      <span className="max-w-[45%] break-words text-right text-lg font-semibold text-[--text-dark-primary]">
        {payment.amount} {currency.symbol}
      </span>
    </Link>
  );
}

export function ProjectPaymentSummary({ project }: { project: ProjectView }) {
  const { currency } = useDaoConfig();
  const account = useAuthStore((state) => state.session?.accountId) ?? '';
  const participants = useProjectParticipants(project.projectId);
  const milestones = executionMilestones(project);
  const payments = projectPayments(project, account);
  const awaiting = payments.filter((p) => !p.paid);
  const paid = payments.filter((p) => p.paid);
  const escrow =
    BigInt(project.planning.escrow) + BigInt(project.executionEscrow);
  return (
    <section aria-label="Project payment summary">
      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          [
            'Total Budget Funded',
            (
              escrow +
              BigInt(
                paymentTotal(
                  projectPayments(project, project.client).filter((p) => p.paid)
                )
              )
            ).toString(),
          ],
          ['Funds Remaining', escrow.toString()],
          ['Awaiting Payment', paymentTotal(awaiting)],
          ['Paid', paymentTotal(paid)],
        ].map(([label, value]) => (
          <Card key={label} className="border-[#3D3D3D] bg-[#231F1F]">
            <CardContent className="p-4">
              <p className="mb-1 text-xs uppercase tracking-wider text-[#9B9B9B]">
                {label}
              </p>
              <p className="break-all text-lg font-bold text-[#F5F5F5]">
                {value} {currency.symbol}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
      {participants.isError && (
        <p role="alert" className="mb-4 text-sm text-[#9B9B9B]">
          Participant names could not be loaded.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => void participants.refetch()}
          >
            Retry participants
          </button>
        </p>
      )}
      <div className="space-y-8">
        {(
          [
            ['Awaiting Payment', awaiting],
            ['Completed and Paid', paid],
          ] as const
        ).map(
          ([title, items]) =>
            items.length > 0 && (
              <div key={title}>
                <div className="mb-4 flex items-center gap-2">
                  <i
                    aria-hidden="true"
                    className={
                      (title === 'Awaiting Payment'
                        ? 'ri-time-line'
                        : 'ri-checkbox-circle-line') + ' text-[#36D399]'
                    }
                  />
                  <h3 className="text-lg font-semibold text-[#F5F5F5]">
                    {title}
                  </h3>
                  <span className="text-sm text-[#9B9B9B]">
                    ({items.length})
                  </span>
                </div>
                <div className="space-y-3">
                  {items.map((payment) => {
                    const index = milestones.findIndex(
                      (m) => m.milestoneId === payment.key
                    );
                    const milestone = milestones[index];
                    const status =
                      payment.key === 'planning'
                        ? project.planning.status
                        : milestone?.status;
                    const assignees = [
                      ...new Set(milestone?.assignments.map((a) => a.worker)),
                    ].map((worker) => {
                      const participant = participants.data?.workers.find(
                        (p) => p.accountId === worker
                      );
                      return (
                        participant?.profiles?.worker?.name ??
                        participant?.displayName ??
                        worker
                      );
                    });
                    return (
                      <Link
                        key={payment.key}
                        to={'/payments/' + project.projectId}
                        className="block rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-[--state-brand-active]"
                      >
                        <Card className="border-[#3D3D3D] bg-[#1A1A1A]">
                          <CardContent className="p-4">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex min-w-0 items-center gap-3">
                                {index >= 0 && (
                                  <span className="text-sm font-bold text-[#9B9B9B]">
                                    #{index + 1}
                                  </span>
                                )}
                                <div className="min-w-0">
                                  <h4 className="truncate text-sm font-medium text-[#F5F5F5]">
                                    {payment.title}
                                  </h4>
                                  <p className="mt-0.5 break-all text-xs text-[#9B9B9B]">
                                    {payment.key === 'planning'
                                      ? 'Coordinator: ' +
                                        (participants.data?.coordinator.profiles
                                          ?.worker?.name ??
                                          participants.data?.coordinator
                                            .displayName ??
                                          project.coordinator)
                                      : 'Assignees: ' +
                                        (assignees.join(', ') || 'Unassigned')}
                                  </p>
                                  {payment.frozen && !payment.paid && (
                                    <p className="mt-0.5 text-xs text-amber-400">
                                      Payment held
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center justify-between gap-3 sm:max-w-[50%] sm:justify-end">
                                <Badge
                                  className={
                                    'h-auto shrink-0 gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium shadow-none ' +
                                    (payment.paid
                                      ? 'border-green-500/30 bg-green-500/20 text-green-400'
                                      : status === 'Disputed'
                                        ? 'border-red-500/30 bg-red-500/20 text-red-400'
                                        : 'border-gray-500/30 bg-gray-500/20 text-gray-400')
                                  }
                                >
                                  <span
                                    className="h-1.5 w-1.5 rounded-full bg-current"
                                    aria-hidden="true"
                                  />
                                  {status?.replace(
                                    /([a-z])([A-Z])/g,
                                    '$1 $2'
                                  ) ?? 'Status unavailable'}
                                </Badge>
                                <p className="min-w-0 break-all text-right text-sm font-semibold text-[#F5F5F5]">
                                  {payment.amount} {currency.symbol}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )
        )}
        {!payments.length && (
          <p className="text-sm text-[#9B9B9B]">
            No funded or settled payments yet.
          </p>
        )}
      </div>
    </section>
  );
}
